import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  Download,
  Building2,
  Calendar,
  Receipt,
  Ticket,
  FolderKanban,
  Printer,
} from 'lucide-react';

export const ReportsPage: React.FC = () => {
  const [activeReportTab, setActiveReportTab] = useState('projects');
  const [projects, setProjects] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/api/projects').then((res) => (res.ok ? res.json() : null)).catch(() => null),
      fetch('/api/invoices').then((res) => (res.ok ? res.json() : null)).catch(() => null),
      fetch('/api/tickets').then((res) => (res.ok ? res.json() : null)).catch(() => null),
    ])
      .then(([p, inv, t]) => {
        if (Array.isArray(p)) setProjects(p);
        if (Array.isArray(inv)) setInvoices(inv);
        if (Array.isArray(t)) setTickets(t);
      })
      .catch((err) => console.warn('Reports fetch notice:', err))
      .finally(() => setLoading(false));
  }, []);

  const formatPKR = (val: string | number) => {
    return 'Rs. ' + Math.round(Number(val || 0)).toLocaleString('en-PK');
  };

  const reportTabs = [
    { id: 'projects', label: 'Project Status & Profitability' },
    { id: 'tickets', label: 'Branch Tickets & Maintenance' },
    { id: 'finance', label: 'Finance & Invoicing Aging' },
    { id: 'ubl', label: 'UBL Regional Performance' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Executive Reports &amp; Analytics
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-900">
              UBL Operations
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Operational summaries, branch-wise status, financial receivables, and project profitability.
          </p>
        </div>

        <button
          onClick={() => window.print()}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg text-xs font-semibold shadow-2xs transition-colors self-start sm:self-auto"
        >
          <Printer className="w-4 h-4 text-slate-500" />
          <span>Print / Export PDF</span>
        </button>
      </div>

      {/* Report Selection Tabs */}
      <div className="flex items-center gap-1.5 overflow-x-auto border-b border-slate-200 pb-1">
        {reportTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveReportTab(tab.id)}
            className={`px-3.5 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
              activeReportTab === tab.id
                ? 'bg-[#0f2942] text-white shadow-2xs'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab 1: Project Status & Profitability */}
      {activeReportTab === 'projects' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden p-5 space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h2 className="text-sm font-bold text-slate-900">Project Performance &amp; Margins</h2>
            <span className="text-xs text-slate-400">Total {projects.length} Active Records</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-3">Project Number</th>
                  <th className="py-2.5 px-3">Branch Location</th>
                  <th className="py-2.5 px-3">PO Amount (PKR)</th>
                  <th className="py-2.5 px-3">Actual Cost (PKR)</th>
                  <th className="py-2.5 px-3">Gross Margin</th>
                  <th className="py-2.5 px-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(Array.isArray(projects) ? projects : []).map((p) => {
                  const profit = Number(p.profit || 0);
                  return (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="py-2.5 px-3 font-bold text-blue-900">{p.projectNumber}</td>
                      <td className="py-2.5 px-3 text-slate-800">{p.branchName}</td>
                      <td className="py-2.5 px-3 font-semibold text-slate-900">{formatPKR(p.contractValue)}</td>
                      <td className="py-2.5 px-3 text-slate-700">{formatPKR(p.actualCost)}</td>
                      <td className="py-2.5 px-3 font-bold text-emerald-700">{formatPKR(profit)}</td>
                      <td className="py-2.5 px-3">
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700">
                          {p.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab 2: Branch Tickets */}
      {activeReportTab === 'tickets' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden p-5 space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h2 className="text-sm font-bold text-slate-900">UBL Branch Work Orders</h2>
            <span className="text-xs text-slate-400">Total {tickets.length} Maintenance Requests</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-3">Ticket #</th>
                  <th className="py-2.5 px-3">Branch Name</th>
                  <th className="py-2.5 px-3">Category</th>
                  <th className="py-2.5 px-3">Priority</th>
                  <th className="py-2.5 px-3">Assigned Engineer</th>
                  <th className="py-2.5 px-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(Array.isArray(tickets) ? tickets : []).map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50">
                    <td className="py-2.5 px-3 font-bold text-blue-900">{t.ticketNumber}</td>
                    <td className="py-2.5 px-3 text-slate-800">{t.branchName}</td>
                    <td className="py-2.5 px-3 text-slate-600">{t.category}</td>
                    <td className="py-2.5 px-3 font-semibold text-amber-700">{t.priority}</td>
                    <td className="py-2.5 px-3 text-slate-700">{t.assignedTo}</td>
                    <td className="py-2.5 px-3">
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800">
                        {t.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab 3: Finance Aging */}
      {activeReportTab === 'finance' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden p-5 space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h2 className="text-sm font-bold text-slate-900">Invoices &amp; Collections Summary</h2>
            <span className="text-xs text-slate-400">Total {invoices.length} Bills</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-3">Invoice #</th>
                  <th className="py-2.5 px-3">Branch</th>
                  <th className="py-2.5 px-3">Issue Date</th>
                  <th className="py-2.5 px-3">Due Date</th>
                  <th className="py-2.5 px-3 text-right">Invoice Total (PKR)</th>
                  <th className="py-2.5 px-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(Array.isArray(invoices) ? invoices : []).map((inv) => (
                  <tr key={inv.id} className="hover:bg-slate-50">
                    <td className="py-2.5 px-3 font-bold text-blue-900">{inv.invoiceNumber}</td>
                    <td className="py-2.5 px-3 text-slate-800">{inv.branchName}</td>
                    <td className="py-2.5 px-3 text-slate-600">{inv.invoiceDate}</td>
                    <td className="py-2.5 px-3 text-slate-600">{inv.dueDate}</td>
                    <td className="py-2.5 px-3 text-right font-bold text-slate-900">{formatPKR(inv.grandTotal)}</td>
                    <td className="py-2.5 px-3">
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                        {inv.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab 4: UBL Regional Performance */}
      {activeReportTab === 'ubl' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-4">
          <h2 className="text-sm font-bold text-slate-900">UBL Regional Project Matrix</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="font-bold text-slate-900 text-sm">South Region (Karachi)</div>
              <div className="text-slate-500 mt-1">2 Active Projects • 2 Completed</div>
              <div className="text-blue-900 font-bold mt-2">Rs. 2,770,000 Volume</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="font-bold text-slate-900 text-sm">Central Region (Punjab)</div>
              <div className="text-slate-500 mt-1">1 Active Project (Gulberg III)</div>
              <div className="text-blue-900 font-bold mt-2">Rs. 1,250,000 Volume</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="font-bold text-slate-900 text-sm">North Region (Islamabad)</div>
              <div className="text-slate-500 mt-1">2 Pending Surveys</div>
              <div className="text-blue-900 font-bold mt-2">Upcoming Mobilization</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
